import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Plus, Trash2, Loader2, Tag } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PromotionsTab = ({ products = [] }) => {
  const { toast } = useToast();
  const [promotions, setPromotions] = useState([]);
  const [promotionsLoading, setPromotionsLoading] = useState(false);
  const [showAddPromotion, setShowAddPromotion] = useState(false);
  const [uploadingPromoImage, setUploadingPromoImage] = useState(false);
  const [newPromotion, setNewPromotion] = useState({
    title: '',
    description: '',
    image_url: '',
    link: '',
    active: true,
    font_family: 'Inter',
    font_size: 'text-3xl',
    text_color: '#ffffff',
    promo_type: 'publicidad',
    product_id: null
  });
  const token = localStorage.getItem('krolbolt_token');

  useEffect(() => {
    fetchPromotions();
  }, []);

  const fetchPromotions = async () => {
    try {
      const response = await fetch(`${API}/promotions?active_only=false`);
      if (response.ok) setPromotions(await response.json());
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return null;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API}/upload/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        return data.url;
      } else {
        throw new Error('Error uploading file');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast({ title: 'Error', description: 'No se pudo subir el archivo', variant: 'destructive' });
      return null;
    }
  };

  const handleAddPromotion = async (e) => {
    e.preventDefault();
    setPromotionsLoading(true);
    try {
      const response = await fetch(`${API}/promotions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newPromotion)
      });

      if (response.ok) {
        toast({ title: 'Éxito', description: 'Promoción agregada correctamente' });
        setNewPromotion({
          title: '',
          description: '',
          image_url: '',
          link: '',
          active: true,
          font_family: 'Inter',
          font_size: 'text-3xl',
          text_color: '#ffffff',
          promo_type: 'publicidad',
          product_id: null
        });
        setShowAddPromotion(false);
        fetchPromotions();
      } else {
        toast({ title: 'Error', description: 'No se pudo agregar la promoción', variant: 'destructive' });
      }
    } catch (error) {
      console.error('Error:', error);
      toast({ title: 'Error', description: 'Error al agregar promoción', variant: 'destructive' });
    }
    setPromotionsLoading(false);
  };

  const handleDeletePromotion = async (id) => {
    if (!window.confirm('¿Eliminar esta promoción?')) return;

    try {
      const response = await fetch(`${API}/promotions/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast({ title: 'Éxito', description: 'Promoción eliminada' });
        fetchPromotions();
      }
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo eliminar', variant: 'destructive' });
    }
  };

  const togglePromotionStatus = async (id, currentStatus) => {
    try {
      const promo = promotions.find(p => p.id === id);
      const response = await fetch(`${API}/promotions/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ...promo, active: !currentStatus })
      });

      if (response.ok) {
        toast({ title: 'Éxito', description: 'Estado actualizado' });
        fetchPromotions();
      }
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo actualizar', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
          <Tag className="h-6 w-6" />
          Gestión de Promociones
        </h2>
        <Button
          onClick={() => setShowAddPromotion(!showAddPromotion)}
          className="bg-gradient-to-r from-cyan-600 to-emerald-600"
        >
          <Plus className="mr-2 h-4 w-4" />
          Agregar Promoción
        </Button>
      </div>

      {showAddPromotion && (
        <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700">
          <CardContent className="p-6">
            <form onSubmit={handleAddPromotion} className="space-y-4">
              <div>
                <Label className="text-slate-300">Título *</Label>
                <Input
                  value={newPromotion.title}
                  onChange={(e) => setNewPromotion({...newPromotion, title: e.target.value})}
                  className="bg-slate-900/50 border-slate-700 text-white"
                  required
                />
              </div>

              <div>
                <Label className="text-slate-300">Tipo de Promoción</Label>
                <select
                  value={newPromotion.promo_type}
                  onChange={(e) => setNewPromotion({...newPromotion, promo_type: e.target.value})}
                  className="w-full p-2 bg-slate-900/50 border border-slate-700 text-white rounded-md"
                >
                  <option value="publicidad">Banner General</option>
                  <option value="producto">Producto Específico</option>
                </select>
              </div>

              {newPromotion.promo_type === 'producto' && (
                <div>
                  <Label className="text-slate-300">Producto</Label>
                  <select
                    value={newPromotion.product_id || ''}
                    onChange={(e) => setNewPromotion({...newPromotion, product_id: e.target.value})}
                    className="w-full p-2 bg-slate-900/50 border border-slate-700 text-white rounded-md"
                    required
                  >
                    <option value="">Seleccione un producto</option>
                    {products.map(p => (
                      <option key={p.id} value={p.id}>{p.name} - ${p.price}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-slate-300">Fuente</Label>
                  <select
                    value={newPromotion.font_family}
                    onChange={(e) => setNewPromotion({...newPromotion, font_family: e.target.value})}
                    className="w-full p-2 bg-slate-900/50 border border-slate-700 text-white rounded-md"
                  >
                    <option value="Inter">Inter (Default)</option>
                    <option value="Arial">Arial</option>
                    <option value="Georgia">Georgia</option>
                    <option value="Times New Roman">Times New Roman</option>
                    <option value="Courier New">Courier New</option>
                    <option value="Verdana">Verdana</option>
                    <option value="Comic Sans MS">Comic Sans MS</option>
                  </select>
                </div>

                <div>
                  <Label className="text-slate-300">Tamaño Texto</Label>
                  <select
                    value={newPromotion.font_size}
                    onChange={(e) => setNewPromotion({...newPromotion, font_size: e.target.value})}
                    className="w-full p-2 bg-slate-900/50 border border-slate-700 text-white rounded-md"
                  >
                    <option value="text-xl">Pequeño</option>
                    <option value="text-2xl">Mediano</option>
                    <option value="text-3xl">Grande (Recomendado)</option>
                    <option value="text-4xl">Muy Grande</option>
                    <option value="text-5xl">Gigante</option>
                  </select>
                </div>

                <div>
                  <Label className="text-slate-300">Color Texto</Label>
                  <Input
                    type="color"
                    value={newPromotion.text_color}
                    onChange={(e) => setNewPromotion({...newPromotion, text_color: e.target.value})}
                    className="h-10 bg-slate-900/50 border-slate-700"
                  />
                </div>
              </div>

              <div>
                <Label className="text-slate-300">Descripción *</Label>
                <Textarea
                  value={newPromotion.description}
                  onChange={(e) => setNewPromotion({...newPromotion, description: e.target.value})}
                  className="bg-slate-900/50 border-slate-700 text-white"
                  required
                />
              </div>

              <div>
                <Label className="text-slate-300">Subir Imagen (JPG/PNG)</Label>
                <div className="flex gap-2 items-center mt-2">
                  <Input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setUploadingPromoImage(true);
                        const url = await handleFileUpload(file);
                        if (url) {
                          setNewPromotion({...newPromotion, image_url: url});
                          toast({ title: 'Imagen subida exitosamente' });
                        }
                        setUploadingPromoImage(false);
                      }
                    }}
                    className="bg-slate-900/50 border-slate-700 text-white"
                  />
                  {uploadingPromoImage && <Loader2 className="h-5 w-5 animate-spin text-cyan-400" />}
                </div>
                {newPromotion.image_url && (
                  <div className="mt-2">
                    <img src={newPromotion.image_url} alt="Preview" className="w-full h-40 object-cover rounded-lg" />
                  </div>
                )}
              </div>

              <div>
                <Label className="text-slate-300">Enlace (opcional)</Label>
                <Input
                  value={newPromotion.link}
                  onChange={(e) => setNewPromotion({...newPromotion, link: e.target.value})}
                  className="bg-slate-900/50 border-slate-700 text-white"
                  placeholder="/shop o URL externa"
                />
              </div>

              <Button type="submit" disabled={promotionsLoading || uploadingPromoImage} className="bg-gradient-to-r from-cyan-600 to-emerald-600">
                {promotionsLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Guardando...</> : 'Agregar Promoción'}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {promotions.map((promo) => (
          <Card key={promo.id} className="bg-slate-800/50 backdrop-blur-sm border-slate-700">
            <CardContent className="p-4">
              {promo.image_url && (
                <img src={promo.image_url} alt={promo.title} className="w-full h-40 object-cover rounded-lg mb-3" />
              )}
              <h3 className="text-white font-bold text-lg mb-2">{promo.title}</h3>
              <p className="text-slate-400 text-sm mb-3">{promo.description}</p>
              {promo.link && (
                <p className="text-cyan-400 text-xs mb-3">🔗 {promo.link}</p>
              )}
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={promo.active ? "default" : "outline"}
                  onClick={() => togglePromotionStatus(promo.id, promo.active)}
                  className={promo.active ? "bg-green-600" : ""}
                >
                  {promo.active ? "Activa" : "Inactiva"}
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleDeletePromotion(promo.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {promotions.length === 0 && (
          <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700 col-span-full">
            <CardContent className="p-12 text-center">
              <Tag className="h-16 w-16 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400 text-lg">No hay promociones creadas</p>
              <p className="text-slate-500 text-sm">Agrega tu primera promoción</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default PromotionsTab;
